import { UserAvatar } from "@/components/ui/UserAvatar";
import { ProgressBar } from "@/components/ui/progress-bar";
import { ProgressRing } from "@/components/ui/progress-ring";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, formatDisplayName, formatLastActivity } from "@/lib/utils";

export interface ParticipantRow {
  userId: string;
  enrollmentId: string;
  firstName: string;
  lastName: string;
  colorIndex: number;
  lastActivityAt: Date | null;
  overallProgressPercent: number;
  currentModuleTitle: string | null;
  currentModuleProgressPercent: number | null;
}

export interface ParticipantsOverviewTableProps {
  participants: ParticipantRow[];
  selectedUserId?: string | null;
  onRowClick?: (participant: ParticipantRow) => void;
}

const STICKY_NAME = "sticky left-0 z-10";
const COMPACT_CELL = "px-3 py-1";
/** Matches StatTile in ParticipantDetailsPanel (`rounded-md`). */
const ROW_SURFACE = "bg-accent";
const ROW_RADIUS_LEFT = "rounded-l-md";
const ROW_RADIUS_RIGHT = "rounded-r-md";

const ProgressModuleCell = ({
  overallProgressPercent,
  currentModuleTitle,
  currentModuleProgressPercent,
}: {
  overallProgressPercent: number;
  currentModuleTitle: string | null;
  currentModuleProgressPercent: number | null;
}) => {
  const isComplete = overallProgressPercent >= 100;
  const ringColor = isComplete ? "var(--success)" : "var(--secondary)";
  const labelColor = isComplete ? "var(--success)" : "var(--foreground)";
  const isModuleComplete = (currentModuleProgressPercent ?? 0) >= 100;
  const fallbackLabel = isComplete ? "Abgeschlossen" : "Noch nicht gestartet";

  return (
    <div className="flex items-center gap-3 w-full pr-4">
      <ProgressRing
        percentage={overallProgressPercent}
        size="lg"
        label={`${Math.round(overallProgressPercent)}%`}
        showLabel
        fillColor={ringColor}
        labelColor={labelColor}
      />
      {currentModuleTitle === null ? (
        <span className="text-xs text-muted-foreground">{fallbackLabel}</span>
      ) : (
        <div className="flex flex-1 min-w-0 flex-col gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs font-semibold text-primary truncate cursor-default">
                {currentModuleTitle}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{currentModuleTitle}</TooltipContent>
          </Tooltip>
          <ProgressBar
            percentage={currentModuleProgressPercent ?? 0}
            size="sm"
            color={isModuleComplete ? "success" : "secondary"}
            showLabel
          />
        </div>
      )}
    </div>
  );
};

export const ParticipantsOverviewTable = ({
  participants,
  selectedUserId,
  onRowClick,
}: ParticipantsOverviewTableProps) => (
  <div
    className={cn(
      "overflow-hidden",
      "lg:flex lg:h-full lg:min-h-0 lg:flex-col",
      "**:data-[slot=table-container]:lg:min-h-0",
      "**:data-[slot=table-container]:lg:flex-1",
      "**:data-[slot=table-container]:lg:overflow-y-auto",
      "**:data-[slot=table-container]:scrollbar-none",
      "**:data-[slot=table-container]:[-ms-overflow-style:none]",
      "**:data-[slot=table-container]:[&::-webkit-scrollbar]:hidden"
    )}
  >
    <div className="flex shrink-0 items-center gap-3 px-4 py-3">
      <h2 className="text-base font-bold text-primary leading-tight shrink-0">
        {participants.length} Lernende
      </h2>
    </div>
    <Table>
      <TableBody>
        {participants.length === 0 && (
          <TableRow className="border-b-0">
            <TableCell
              colSpan={2}
              className="text-center text-sm text-muted-foreground py-6"
            >
              Keine Lernende vorhanden.
            </TableCell>
          </TableRow>
        )}
        {participants.map((p) => {
          const isSelected = selectedUserId === p.userId;
          return (
            <TableRow
              key={p.userId}
              className={cn(
                "group border-b-0",
                onRowClick && "cursor-pointer hover:bg-transparent"
              )}
              onClick={onRowClick ? () => onRowClick(p) : undefined}
            >
              <TableCell
                className={cn(
                  STICKY_NAME,
                  COMPACT_CELL,
                  "w-45 min-w-45 transition-colors",
                  isSelected
                    ? cn(ROW_SURFACE, ROW_RADIUS_LEFT)
                    : cn(
                        "bg-background",
                        onRowClick &&
                          "group-hover:bg-accent group-hover:rounded-l-md"
                      )
                )}
              >
                <div className="flex items-center gap-2">
                  <UserAvatar
                    firstName={p.firstName}
                    lastNameInitial={p.lastName[0]}
                    colorIndex={p.colorIndex}
                    size="md"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">
                      {formatDisplayName(p.firstName, p.lastName[0])}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {formatLastActivity(p.lastActivityAt)}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell
                className={cn(
                  "align-middle transition-colors",
                  isSelected && cn(ROW_SURFACE, ROW_RADIUS_RIGHT),
                  onRowClick &&
                    !isSelected &&
                    "group-hover:bg-accent group-hover:rounded-r-md"
                )}
              >
                <ProgressModuleCell
                  overallProgressPercent={p.overallProgressPercent}
                  currentModuleTitle={p.currentModuleTitle}
                  currentModuleProgressPercent={p.currentModuleProgressPercent}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);
